import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Chip,
  RadioGroup,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SecurityIcon from "@mui/icons-material/Security";
import { useNotify } from "react-admin";
import { rolesApi, permissionsApi, userApi, agentsApi } from "../providers/env";
import { PermissionOut, RoleOut, AgentOut } from "../generated";

interface EditRoleModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  role: RoleOut | null;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  open,
  onClose,
  onSuccess,
  role,
}) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allPermissions, setAllPermissions] = useState<PermissionOut[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [form, setForm] = useState({
    name: "",
    description: "",
  });
  // Agent access state
  const [agents, setAgents] = useState<AgentOut[]>([]);
  const [hasAllAgentAccess, setHasAllAgentAccess] = useState(true);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);

  // Check if current user is super admin
  const isSuperAdmin = (() => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        return userInfo.is_superuser || false;
      }
    } catch {
      return false;
    }
    return false;
  })();

  useEffect(() => {
    if (open && role) {
      fetchPermissions();
      fetchAgents();
      setForm({
        name: role.name || "",
        description: role.description || "",
      });
      // Set selected permissions from role
      const permIds = role.permissions?.map((p) => p.id) || [];
      setSelectedPermissions(permIds);
      // Set agent access from role
      setHasAllAgentAccess(role.has_all_agent_access ?? true);
      setSelectedAgentIds(role.agent_ids || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  // Auto-add dashboard view and agent view permissions when permissions are loaded
  useEffect(() => {
    if (allPermissions.length > 0) {
      const dashboardViewPerm = allPermissions.find(
        (p) => p.resource === 'dashboard' && p.action === 'view'
      );
      const agentViewPerm = allPermissions.find(
        (p) => p.resource === 'agent' && p.action === 'view'
      );

      const requiredPermIds = [dashboardViewPerm?.id, agentViewPerm?.id].filter(Boolean) as number[];

      if (requiredPermIds.length > 0) {
        setSelectedPermissions((prev) => {
          const newPerms = [...prev];
          requiredPermIds.forEach(id => {
            if (!newPerms.includes(id)) {
              newPerms.push(id);
            }
          });
          return newPerms;
        });
      }
    }
  }, [allPermissions]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsApi.getAllPermissions();
      setAllPermissions(response.data || []);
    } catch (error: any) {
      notify("Error loading permissions", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      // Fetch agents for the role's organization
      if (role?.organization_id) {
        const response = await agentsApi.getAgentsByOrganization({ organizationId: role.organization_id });
        setAgents(response.data || []);
      } else {
        const response = await agentsApi.getAgents();
        setAgents(response.data || []);
      }
    } catch (error: any) {
      console.error("Error loading agents", error);
      setAgents([]);
    }
  };

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {} as Record<string, PermissionOut[]>);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePermissionToggle = (permissionId: number) => {
    const permission = allPermissions.find((p) => p.id === permissionId);
    if (!permission) return;

    // Prevent toggling ANY dashboard permission
    if (permission.resource === 'dashboard') {
      return;
    }

    // Prevent toggling agent view permission
    if (permission.resource === 'agent' && permission.action === 'view') {
      return;
    }

    setSelectedPermissions((prev) => {
      const isRemoving = prev.includes(permissionId);

      if (isRemoving) {
        // Removing permission
        return prev.filter((id) => id !== permissionId);
      } else {
        // Adding permission
        const newPermissions = [...prev, permissionId];

        // If adding edit, delete, or create, also add view permission
        if (['edit', 'delete', 'create', 'clone'].includes(permission.action)) {
          const viewPermission = allPermissions.find(
            (p) => p.resource === permission.resource && p.action === 'view'
          );

          if (viewPermission && !newPermissions.includes(viewPermission.id)) {
            newPermissions.push(viewPermission.id);
          }
        }

        return newPermissions;
      }
    });
  };

  const handleResourceToggle = (resource: string) => {
    // Prevent toggling dashboard resource
    if (resource === 'dashboard') {
      return;
    }

    // For agent resource, exclude view permission from toggle
    let resourcePermIds = groupedPermissions[resource].map((p) => p.id);
    if (resource === 'agent') {
      const agentViewPerm = allPermissions.find(
        (p) => p.resource === 'agent' && p.action === 'view'
      );
      if (agentViewPerm) {
        resourcePermIds = resourcePermIds.filter(id => id !== agentViewPerm.id);
      }
    }

    const allSelected = resourcePermIds.every((id) =>
      selectedPermissions.includes(id)
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !resourcePermIds.includes(id))
      );
    } else {
      setSelectedPermissions((prev) =>
        Array.from(new Set([...prev, ...resourcePermIds]))
      );
    }
  };

  const handleSelectAll = () => {
    setSelectedPermissions(allPermissions.map((p) => p.id));
  };

  const handleDeselectAll = () => {
    // Keep dashboard permissions and agent view selected when deselecting all
    const requiredPermIds = allPermissions
      .filter((p) => p.resource === 'dashboard' || (p.resource === 'agent' && p.action === 'view'))
      .map((p) => p.id);
    setSelectedPermissions(requiredPermIds);
  };

  const refreshCurrentUserPermissions = async () => {
    try {
      // Get current user info from localStorage
      const permStr = localStorage.getItem('permissions');
      if (!permStr) return;

      const currentUser = JSON.parse(permStr);

      // Check if current user has the role being updated
      if (currentUser.role_id === role?.id) {
        // Fetch updated user identity
        const response = await userApi.usersCurrentUser();
        if (response.data) {
          localStorage.setItem('permissions', JSON.stringify(response.data));
          notify("Your permissions have been updated. Refreshing page...", { type: "info" });

          // Reload page after a short delay to show the notification
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error refreshing permissions:", error);
    }
  };

  const handleSubmit = async () => {
    if (!role) return;

    if (!form.name.trim()) {
      notify("Role name is required", { type: "warning" });
      return;
    }

    // Ensure dashboard:view and agent:view are always included
    const dashboardViewPerm = allPermissions.find(
      (p) => p.resource === 'dashboard' && p.action === 'view'
    );
    const agentViewPerm = allPermissions.find(
      (p) => p.resource === 'agent' && p.action === 'view'
    );

    const requiredPermIds = [dashboardViewPerm?.id, agentViewPerm?.id].filter(Boolean) as number[];
    const finalPermissions = Array.from(new Set([...selectedPermissions, ...requiredPermIds]));

    setSaving(true);
    try {
      await rolesApi.updateRole({
        roleId: role.id,
        roleUpdate: {
          name: form.name,
          description: form.description,
          permission_ids: finalPermissions,
          role_data: role.user_level,  // Keep existing user_level
          has_all_agent_access: hasAllAgentAccess,
          agent_ids: hasAllAgentAccess ? [] : selectedAgentIds,
        },
      });

      // Check if current user's role was updated
      const permStr = localStorage.getItem('permissions');
      const currentUser = permStr ? JSON.parse(permStr) : null;
      const isOwnRole = currentUser?.role_id === role?.id;

      if (isOwnRole) {
        // Refresh permissions if current user's role was updated
        await refreshCurrentUserPermissions();
      } else {
        // Show message for other role updates
        notify("Role updated successfully. Users with this role need to refresh their browser to see the changes.", { type: "success" });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error updating role", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatResourceName = (resource: string) => {
    return resource
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isSystemRole = role?.is_system_role || false;
  // Super admin can edit everything, others cannot edit system roles
  const isEditDisabled = isSystemRole && !isSuperAdmin;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <SecurityIcon sx={{ color: "#1976d2" }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Role: {role?.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: "0.875rem" }}
          >
            Update role details and permissions
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isSystemRole && (
              <Alert
                severity={isSuperAdmin ? "info" : "warning"}
                sx={{
                  mb: 2,
                  borderRadius: 1,
                }}
              >
                {isSuperAdmin 
                  ? "This is a system role. As Super Admin, you can modify it."
                  : "This is a system role and cannot be modified. System roles are created automatically for each organization."
                }
              </Alert>
            )}

            <TextField
              margin="normal"
              label="Role Name"
              name="name"
              placeholder="Enter role name"
              fullWidth
              required
              value={form.name}
              onChange={handleChange}
              disabled={isEditDisabled}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            <TextField
              margin="normal"
              label="Description"
              name="description"
              placeholder="Enter role description"
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={handleChange}
              disabled={isEditDisabled}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            {role?.user_level === "super_admin" && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  mt: 2,
                  borderRadius: 1,
                  "& .MuiAlert-icon": {
                    alignItems: "center"
                  }
                }}
              >
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Org Admin Role
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This role automatically has all permissions enabled by default. No manual permission selection needed.
                </Typography>
              </Alert>
            )}

            {/* Agent Access Section - Hide for super_admin */}
            {role?.user_level !== "super_admin" && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  mb: 2
                }}
              >
                Agent Access
              </Typography>
              <RadioGroup
                value={hasAllAgentAccess ? "all" : "specific"}
                onChange={(e) => {
                  const isAll = e.target.value === "all";
                  setHasAllAgentAccess(isAll);
                  if (isAll) {
                    setSelectedAgentIds([]);
                  } else {
                    // Remove agent create/edit/delete/clone permissions, keep only view
                    const agentNonViewPerms = allPermissions
                      .filter((p) => p.resource === "agent" && p.action !== "view")
                      .map((p) => p.id);
                    setSelectedPermissions((prev) =>
                      prev.filter((id) => !agentNonViewPerms.includes(id))
                    );
                  }
                }}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio disabled={isEditDisabled} />}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                    m: 0,
                    "&:hover": { backgroundColor: "#f9f9f9" }
                  }}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        All Agents
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User can access all agents in the organization
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="specific"
                  control={<Radio disabled={isEditDisabled} />}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1.5,
                    m: 0,
                    "&:hover": { backgroundColor: "#f9f9f9" }
                  }}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Specific Agents
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        User can only access selected agents
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>

              {!hasAllAgentAccess && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                    Select agents ({selectedAgentIds.length} selected)
                  </Typography>
                  {agents.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No agents available
                    </Typography>
                  ) : (
                    <Box sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 1.5
                    }}>
                      {agents.map((agent) => {
                        const isSelected = selectedAgentIds.includes(agent.id);
                        return (
                          <Box
                            key={agent.id}
                            onClick={() => {
                              if (isEditDisabled) return;
                              if (isSelected) {
                                setSelectedAgentIds((prev) => prev.filter((id) => id !== agent.id));
                              } else {
                                setSelectedAgentIds((prev) => [...prev, agent.id]);
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              p: 1.5,
                              border: isSelected ? "2px solid #1976d2" : "1px solid #e0e0e0",
                              borderRadius: 1,
                              cursor: isEditDisabled ? "not-allowed" : "pointer",
                              backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                              transition: "all 0.2s",
                              "&:hover": {
                                backgroundColor: isEditDisabled ? "transparent" : (isSelected ? "#bbdefb" : "#f5f5f5"),
                                borderColor: isEditDisabled ? "#e0e0e0" : "#1976d2",
                              },
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={isEditDisabled}
                              size="small"
                              sx={{ p: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isSelected ? 500 : 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {agent.name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            )}

            {role?.user_level !== "super_admin" && (
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  Permissions ({selectedPermissions.length} selected)
                </Typography>
                {!isSystemRole && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={handleSelectAll}
                      sx={{ textTransform: "none" }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={handleDeselectAll}
                      sx={{ textTransform: "none" }}
                    >
                      Deselect All
                    </Button>
                  </Box>
                )}
              </Box>

              {Object.entries(groupedPermissions).map(
                ([resource, permissions]) => {
                  // Hide 'role' and 'user' resources for User-level roles
                  const isUserRole = role?.name === "User";
                  if (isUserRole && (resource === "role" || resource === "user")) {
                    return null;
                  }

                  const resourcePermIds = permissions.map((p) => p.id);
                  const allChecked = resourcePermIds.every((id) =>
                    selectedPermissions.includes(id)
                  );
                  const someChecked =
                    resourcePermIds.some((id) =>
                      selectedPermissions.includes(id)
                    ) && !allChecked;

                  return (
                    <Accordion key={resource} defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allChecked}
                              indeterminate={someChecked}
                              disabled={isEditDisabled}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleResourceToggle(resource);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {formatResourceName(resource)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {
                                  resourcePermIds.filter((id) =>
                                    selectedPermissions.includes(id)
                                  ).length
                                }{" "}
                                / {permissions.length} selected
                              </Typography>
                            </Box>
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{
                            pl: 4,
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: 1,
                          }}
                        >
                          {permissions.map((perm) => {
                            const isDashboardPerm = perm.resource === 'dashboard';
                            const isAgentView = perm.resource === 'agent' && perm.action === 'view';
                            const isAgentNonView = perm.resource === 'agent' && perm.action !== 'view';
                            const isLockedBySpecificAgents = isAgentNonView && !hasAllAgentAccess;
                            const isLocked = isDashboardPerm || isAgentView || isLockedBySpecificAgents;

                            return (
                              <FormControlLabel
                                key={perm.id}
                                control={
                                  <Checkbox
                                    checked={selectedPermissions.includes(
                                      perm.id
                                    )}
                                    disabled={isEditDisabled || isLocked}
                                    onChange={() =>
                                      handlePermissionToggle(perm.id)
                                    }
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2">
                                      {perm.action.charAt(0).toUpperCase() +
                                        perm.action.slice(1)}
                                      {(isDashboardPerm || isAgentView) && (
                                        <Chip
                                          label="Required"
                                          size="small"
                                          sx={{
                                            ml: 1,
                                            height: 18,
                                            fontSize: "0.65rem",
                                            backgroundColor: "#4caf50",
                                            color: "white",
                                          }}
                                        />
                                      )}
                                    </Typography>
                                    {isDashboardPerm && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", fontStyle: "italic", fontSize: "0.7rem" }}
                                      >
                                        Dashboard permissions cannot be changed
                                      </Typography>
                                    )}
                                    {isAgentView && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", fontStyle: "italic", fontSize: "0.7rem" }}
                                      >
                                        Required for all roles
                                      </Typography>
                                    )}
                                    {isLockedBySpecificAgents && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", fontStyle: "italic", fontSize: "0.7rem" }}
                                      >
                                        Not available with specific agent access
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                }
              )}
            </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{
            textTransform: "none",
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || isEditDisabled}
          variant="contained"
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            textTransform: "none",
            px: 2,
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            "&:disabled": {
              backgroundColor: "#9e9e9e",
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};




